import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { Client, Job } from '@workflow/contracts'
import { ActionMenu, type ActionMenuSection, Button, Dropdown, EmptyNote, FADE, Field, Icons, Loading, StateNote, StatusLabel, TextArea, TextField, Truncate } from '@workflow/ui'
import { describeError, errorSentence } from '@/lib/api/client'
import {
  useArchiveClient,
  useClient,
  useDeleteClient,
  useClientPayments,
  useClientTotals,
  useJobs,
  useRecordPayment,
  useRestoreClient,
  useSession,
  useSetClientStatus,
  useVoidPayment,
} from '@/lib/api/hooks'
import { formatTimestamp } from '@/lib/format'
import { Fact } from '@/shell/Fact'
import { PageFrame, Section } from '@/shell/PageFrame'
import { navigate } from '@/app/router'
import { CLIENT_STATUS } from './clientStatus'
import { JOB_STATUS } from '@/features/jobs/jobsStatus'
import { JobCreateDialog } from '@/features/jobs/JobCreateDialog'
import { ClientWorkflows } from './ClientWorkflows'
import { ClientForm } from './ClientForm'
import { JobNoteComposer, JobNotes } from '@/features/jobs/JobNotes'
import { MoneyTotalsStrip, MoneyTrail } from '@/features/jobs/JobMoney'

/**
 * What this client has given: the totals and the trail, plus one form —
 * money in, against the client, naming a job when the work is known. The
 * outbound side of the ledger lives on the jobs, where the players are.
 */
function ClientMoney({ client, jobs, jobsLoading }: { client: Client; jobs: Job[]; jobsLoading: boolean }) {
  const totals = useClientTotals(client.id)
  const payments = useClientPayments(client.id)
  const record = useRecordPayment()
  const voidPayment = useVoidPayment()
  const [recording, setRecording] = useState(false)
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState('BDT')
  const [jobId, setJobId] = useState('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [method, setMethod] = useState('')
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')
  const [attempted, setAttempted] = useState(false)

  const amountInvalid = amount.trim() === '' || !Number.isFinite(Number(amount)) || Number(amount) <= 0
  const error = attempted && amountInvalid ? 'How much, in numbers?' : undefined

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    setAttempted(true)
    if (amountInvalid) return
    record.mutate(
      {
        direction: 'inbound',
        amount: Math.round(Number(amount) * 100),
        currency,
        clientId: client.id,
        ...(jobId ? { jobId } : {}),
        paymentDate: date,
        ...(method.trim() ? { method: method.trim() } : {}),
        ...(reference.trim() ? { referenceNumber: reference.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      },
      { onSuccess: () => setRecording(false) },
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <MoneyTotalsStrip totals={totals.data} loading={totals.isLoading} />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="secondary" icon={<Icons.plus />} onClick={() => setRecording(!recording)}>
          Add income
        </Button>
      </div>
      {recording ? (
        <form onSubmit={submit} className="flex flex-col gap-4 rounded-2xl border bg-surface-sunken px-5 py-5">
          <p className="text-micro text-muted-foreground uppercase">Income — from {client.name}</p>
          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <Field label="Amount" htmlFor="client-payment-amount" error={error}>
              <div className="flex items-center gap-2">
                <TextField
                  id="client-payment-amount"
                  inputMode="decimal"
                  placeholder="e.g. 1200"
                  value={amount}
                  onChange={(event) =>
                    setAmount(event.target.value === '' || Number.isFinite(Number(event.target.value)) ? event.target.value : amount)
                  }
                />
                <Dropdown
                  ariaLabel="Currency"
                  className="w-28"
                  value={currency}
                  options={[
                    { value: 'BDT', label: 'BDT' },
                    { value: 'USD', label: 'USD' },
                    { value: 'EUR', label: 'EUR' },
                    { value: 'GBP', label: 'GBP' },
                    { value: 'INR', label: 'INR' },
                  ]}
                  onChange={setCurrency}
                />
              </div>
            </Field>
            <Field label="Job" htmlFor="client-payment-job" hint="The work the money is for, when it is known.">
              {jobsLoading ? (
                <Loading label="Loading jobs" />
              ) : (
                <Dropdown
                  id="client-payment-job"
                  ariaLabel="Job"
                  placeholder="No job named"
                  value={jobId}
                  options={[
                    { value: '', label: 'No job named' },
                    ...jobs.filter((job) => job.clientId === client.id).map((job) => ({ value: job.id, label: job.title })),
                  ]}
                  onChange={setJobId}
                />
              )}
            </Field>
            <Field label="Date" htmlFor="client-payment-date">
              <TextField
                id="client-payment-date"
                placeholder="YYYY-MM-DD"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </Field>
            <Field label="Method" htmlFor="client-payment-method" hint="bKash, Nagad, bank transfer…">
              <TextField id="client-payment-method" value={method} onChange={(event) => setMethod(event.target.value)} />
            </Field>
            <Field label="Reference" htmlFor="client-payment-reference" hint="Transaction or receipt number (optional)">
              <TextField id="client-payment-reference" value={reference} onChange={(event) => setReference(event.target.value)} />
            </Field>
            <Field label="Notes" htmlFor="client-payment-notes" className="sm:col-span-2">
              <TextArea id="client-payment-notes" rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" variant="primary" size="sm" busy={record.isPending}>
              Save income
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setRecording(false)}>
              Cancel
            </Button>
            {record.error ? <p className="text-caption text-danger">{errorSentence(record.error)}</p> : null}
          </div>
        </form>
      ) : null}
      <MoneyTrail
        payments={payments.data}
        loading={payments.isLoading}
        onVoid={({ id, reason }) => voidPayment.mutate({ id, reason })}
        voiding={voidPayment.isPending}
      />
    </div>
  )
}

/**
 * A client's page: the record as it stands, editable in place — the whole
 * point of mid-edit — and the status decisions, named for themselves. The
 * client's jobs gather here, and the money: what they have given, and the
 * trail of it — the first question of the ledger, asked where the answer
 * lives.
 */
export function ClientDetailPage({ id }: { id: string }) {
  const client = useClient(id)
  const jobs = useJobs()
  const [editing, setEditing] = useState(false)
  const [addingJob, setAddingJob] = useState(false)

  if (client.isLoading) return <Loading label="Loading client" />
  if (client.error || !client.data) {
    return (
      <PageFrame title="Client">
        <StateNote
          title="This client could not be loaded."
          detail={describeError(client.error)}
          action={
            <Button size="sm" icon={<Icons.retry />} onClick={() => void client.refetch()}>
              Try again
            </Button>
          }
        />
      </PageFrame>
    )
  }

  const record = client.data
  const shown = CLIENT_STATUS[record.status]

  return (
    <PageFrame
      title={record.name}
      eyebrow={<span className="font-mono text-caption text-muted-foreground">{record.id}</span>}
      description={record.companyName ?? undefined}
      actions={
        <>
          <StatusLabel tone={shown.tone} label={shown.label} />
          <Button size="sm" onClick={() => setEditing((current) => !current)}>
            {editing ? 'Close' : 'Edit'}
          </Button>
          <ClientActions client={record} jobCount={(jobs.data ?? []).filter((job) => job.clientId === record.id).length} />
        </>
      }
    >
      <AnimatePresence initial={false}>
        {editing ? (
          <motion.section {...FADE} className="mb-4 rounded-2xl border bg-surface px-5 shadow-chip">
            <ClientForm initial={record} onSaved={() => setEditing(false)} onCancel={() => setEditing(false)} />
          </motion.section>
        ) : null}
      </AnimatePresence>

      <Section title="The record">
        <dl className="grid grid-cols-1 gap-y-0.5 text-ui sm:grid-cols-[10rem_1fr] sm:gap-x-4 sm:gap-y-2">
          <Fact label="Company" value={record.companyName} />
          <Fact label="Discord server" value={record.discordServerName} />
          <Fact label="Discord server ID" value={record.discordServerId} mono />
          <Fact label="Contact person" value={record.contactPerson} />
          <Fact label="Contact email" value={record.contactEmail} />
          <Fact label="Contact phone" value={record.contactPhone} />
          <Fact label="Website" value={record.websiteUrl} />
          <Fact label="Country" value={record.countryCode ? record.countryCode.toUpperCase() : null} />
          <Fact label="Timezone" value={record.timezone} />
          <Fact label="Rating" value={record.rating === null ? null : `${record.rating} / 5`} />
          <Fact label="Notes" value={record.notes} pre />
          <Fact label="Created" value={formatTimestamp(record.createdAt)} />
          <Fact label="Updated" value={formatTimestamp(record.updatedAt)} />
        </dl>
      </Section>

      <Section title="Payments">
        <ClientMoney client={record} jobs={jobs.data ?? []} jobsLoading={jobs.isLoading} />
      </Section>

      <Section title="Notes">
        <JobNoteComposer entityType="client" entityId={record.id} className="-mt-3 mb-2" />
        <JobNotes entityType="client" entityId={record.id} />
      </Section>

      <Section title="Workflows">
        <ClientWorkflows client={record} />
      </Section>

      <Section
        title="Jobs for this client"
        aside={
          <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setAddingJob(true)}>
            Add job
          </Button>
        }
      >
        {(() => {
          const jobsForClient = (jobs.data ?? []).filter((job) => job.clientId === record.id)
          if (jobs.isLoading) return <Loading label="Loading jobs" />
          if (jobsForClient.length === 0) {
            return (
              <EmptyNote
                title="No jobs yet."
                detail="The first job of this client is one button away."
                action={
                  <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setAddingJob(true)}>
                    Add job
                  </Button>
                }
              />
            )
          }
          return (
            <ul className="flex flex-col">
              {jobsForClient.map((job) => {
                const shown = JOB_STATUS[job.status]
                return (
                  <li key={job.id} className="border-b border-hairline last:border-0">
                    <button
                      type="button"
                      onClick={() => navigate({ name: 'job', id: job.id })}
                      className="-mx-2 flex w-full items-center gap-3 rounded-md px-2 py-3 text-left transition-colors duration-fast hover:bg-tint"
                    >
                      <span className="min-w-0 flex-1">
                        <Truncate text={job.title} className="text-ui font-medium text-foreground" />
                        <span className="block truncate text-caption text-muted-foreground">{job.typeName}</span>
                      </span>
                      <StatusLabel tone={shown.tone} hue={shown.hue} label={shown.label} />
                      <span className="inline-flex rotate-90 items-center text-muted-foreground">
                        <Icons.disclosure />
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )
        })()}
      </Section>

      {/* The slate opens already knowing its client: the who is answered, so
          it asks only what the work is, what kind it is, and how loud. */}
      <JobCreateDialog open={addingJob} onClose={() => setAddingJob(false)} initialClientId={record.id} />
    </PageFrame>
  )
}

/**
 * The client's verbs, behind the same trigger the jobs use: the working
 * toggle, the archive pair, and — for an administrator — deletion, set
 * apart below the rest. Editing stays out as a button; the rest waits one
 * click away. Refusals speak through the toast.
 *
 * Deletion's confirm says what stays: the client's jobs keep standing and
 * read their name marked as a deleted client, so the second click is made
 * knowing it.
 */
function ClientActions({ client, jobCount }: { client: Client; jobCount: number }) {
  const setStatus = useSetClientStatus()
  const archive = useArchiveClient()
  const restore = useRestoreClient()
  const remove = useDeleteClient(() => navigate({ name: 'clients' }, { replace: true }))
  const isAdmin = useSession().data?.account?.role === 'admin'
  const busy = setStatus.isPending || archive.isPending || restore.isPending || remove.isPending

  const sections: ActionMenuSection[] = [{
    items: client.status === 'archived'
      ? [{ key: 'restore', label: 'Restore', icon: <Icons.restore size="sm" />, disabled: busy, onSelect: () => restore.mutate(client.id) }]
      : [
          client.status === 'active'
            ? { key: 'inactive', label: 'Mark Inactive', icon: <Icons.deactivate size="sm" />, disabled: busy, onSelect: () => setStatus.mutate({ id: client.id, status: 'inactive' }) }
            : { key: 'active', label: 'Mark Active', icon: <Icons.activate size="sm" />, disabled: busy, onSelect: () => setStatus.mutate({ id: client.id, status: 'active' }) },
          { key: 'archive', label: 'Archive', icon: <Icons.archive size="sm" />, danger: true, confirmLabel: 'Confirm archive', disabled: busy, onSelect: () => archive.mutate(client.id) },
        ],
  }]
  if (isAdmin) {
    sections.push({
      items: [{
        key: 'delete',
        label: 'Delete',
        icon: <Icons.trash size="sm" />,
        danger: true,
        ask: {
          title: `Delete ${client.name}?`,
          detail: `${jobCount > 0
            ? `Their ${jobCount} ${jobCount === 1 ? 'job and its payments stay' : 'jobs and their payments stay'}, marked as a deleted client. `
            : ''}Their notes and workflows go. This cannot be undone.`,
          confirmLabel: 'Delete',
        },
        disabled: busy,
        onSelect: () => remove.mutate({ id: client.id, name: client.name }),
      }],
    })
  }
  return <ActionMenu label={`Actions for ${client.name}`} trigger="labeled" sections={sections} />
}
